import type { GpuContext } from '@design-ai/gpu-core';
import {
  OnnxModel,
  fetchModel,
  initRuntime,
  type DownloadProgress,
  type ModelEntry,
} from '@design-ai/inference';
import { Workbench } from '@design-ai/pipeline';
import { ORT_WASM_PATHS } from './ort-assets.js';

export interface Engine {
  ctx: GpuContext;
  workbench: Workbench;
}

/**
 * Boots WebGPU and ONNX Runtime, in that order.
 *
 * `initRuntime` creates the device and hands it to ORT before any session
 * exists, which is the only point at which ORT accepts an external device. Get
 * this wrong and every tensor silently starts round-tripping through CPU memory.
 */
export async function bootEngine(): Promise<Engine> {
  const ctx = await initRuntime({
    wasmPaths: ORT_WASM_PATHS,
    // Threads would need cross-origin isolation, which would in turn block
    // cross-origin model downloads. The WebGPU EP does not use them.
    numThreads: 1,
    logLevel: 'warning',
    onDeviceLost: (info) => {
      console.error('[design-ai] GPU device lost:', info.reason, info.message);
    },
  });
  return { ctx, workbench: new Workbench(ctx) };
}

export interface ModelLoadState {
  status: 'idle' | 'downloading' | 'compiling' | 'ready' | 'error';
  progress: number | null;
  message?: string;
}

/**
 * Downloads (or reads from cache) and compiles a model.
 *
 * Session creation is where ORT compiles hundreds of WGSL kernels, so it is
 * reported as its own phase - on a cold load it can take longer than the
 * download on a fast connection.
 */
export async function loadModel(
  entry: ModelEntry,
  onState: (state: ModelLoadState) => void,
  signal?: AbortSignal,
): Promise<OnnxModel> {
  onState({ status: 'downloading', progress: 0 });

  const bytes = await fetchModel(entry.url, {
    signal,
    onProgress: (progress: DownloadProgress) => {
      onState({
        status: 'downloading',
        progress: progress.fraction,
        message: progress.fromCache ? 'from cache' : formatBytes(progress.loaded, progress.total),
      });
    },
  });

  onState({ status: 'compiling', progress: null, message: 'compiling shaders' });
  const model = await OnnxModel.create(bytes, { label: entry.id, outputsOnGpu: true });
  onState({ status: 'ready', progress: 1 });
  return model;
}

function formatBytes(loaded: number, total: number | null): string {
  const mb = (value: number): string => `${(value / 1024 / 1024).toFixed(0)} MB`;
  return total ? `${mb(loaded)} / ${mb(total)}` : mb(loaded);
}
